<template lang="html">
  <prt-modal class="prt-add-expense" :show="show" @close="cancel">
    <transition name="slide" appear>
      <section class="card">
        <input type="text" placeholder="Description" tabindex="1" v-model="description" @keyup.enter="$refs.amountInput.focus()" @input="descriptionClean = false" @submit="$refs.amountInput.focus()">
        <div class="amount-wrapper">
          <button aria-label="Set amount to negative" class="icon-selector-button" :class="{active: sign === '-'}" @click="sign = '-'; $refs.amountInput.focus()">
            <prt-icon icon="subtract" />
          </button>
          <button aria-label="Set amount to positive" class="icon-selector-button" :class="{active: sign === '+'}" @click="sign = '+'; $refs.amountInput.focus()">
            <prt-icon icon="add" />
          </button>
          <input autofocus placeholder="Amount" ref="amountInput" step=".01" tabindex="2" type="number" v-model.number="amount" @keyup.enter="addExpense" @submit="addExpense">
        </div>
        <div class="icon-wrapper">
          <div class="icons">
            <button :aria-label="`Transaction icon ${icon}`" class="icon-selector-button" :class="{active: icon === i}" :key="i" v-for="i in icons" @click="setIcon(i)">
              <prt-icon :icon="i" />
            </button>
          </div>
          <div class="fader left" />
          <div class="fader right" />
        </div>
        <div class="button-wrapper">
          <button aria-label="Cancel" class="cancel-button" @click="cancel">Cancel</button>
          <button aria-label="Add Transaction" class="add-button" :disabled="description.length <= 0 || amount === 0 || !amount" @click="addExpense">Add</button>
        </div>
      </section>
    </transition>
  </prt-modal>
</template>

<script>
import PrtModal from './PrtModal.vue';
import PrtIcon from './PrtIcon.vue';

export default {
  components: {
    PrtModal,
    PrtIcon,
  },
  computed: {
    icons() {
      return this.$store.state.user.iconOrder;
    },
    positiveDefaults() {
      return this.$store.state.preferences.positiveDefaults;
    },
  },
  data() {
    return {
      description:
        this.$store.state.preferences.defaultDescriptions[this.$store.state.user.iconOrder[0]],
      descriptionClean: true,
      icon: this.$store.state.user.iconOrder[0],
      amount: null,
      sign: this.$store.state.preferences.positiveDefaults.includes(this.$store.state.user.iconOrder[0]) ? '+' : '-',
    };
  },
  methods: {
    addExpense() {
      const transaction = {
        icon: this.icon,
        description: this.description,
        amount: this.sign === '-' ? Math.abs(this.amount) * -1 : Math.abs(this.amount),
      };

      this.$store.dispatch('addTransaction', transaction).catch(err => this.$store.commit('addToast', { content: `Error saving transaction: ${err.message}`, color: 'negative', timeout: 5000 }));
      this.$store.dispatch('updateIconOrder', this.icon).catch(err => this.$store.commit('addToast', { content: `Error saving user data: ${err.message}`, color: 'negative', timeout: 5000 }));

      this.reset();
      this.$emit('close');
    },
    cancel() {
      this.reset();
      this.$emit('close');
    },
    reset() {
      this.description =
        this.$store.state.preferences.defaultDescriptions[this.$store.state.user.iconOrder[0]];
      this.descriptionClean = true;
      [this.icon] = this.$store.state.user.iconOrder;
      this.amount = null;
      this.sign = this.positiveDefaults.includes(this.$store.state.user.iconOrder[0]) ? '+' : '-';
    },
    setIcon(i) {
      this.icon = i;
      if (this.descriptionClean || this.description.length <= 0) {
        this.description = this.$store.state.preferences.defaultDescriptions[i];
        if (this.positiveDefaults.includes(i)) this.sign = '+';
        else this.sign = '-';
        if (!this.descriptionClean) this.descriptionClean = true;
      }
      this.$refs.amountInput.focus();
    },
  },
  props: {
    show: Boolean,
  },
  watch: {
    amount(newVal) {
      if (this.sign !== '-' && newVal < 0) this.sign = '-';
    },
    icons() {
      this.reset();
    },
    positiveDefaults() {
      this.reset();
    },
    show(newVal) {
      if (newVal) this.$nextTick(() => this.$refs.amountInput.focus());
    },
  },
};
</script>

<style lang="stylus" scoped>
  @require '../../styles/colors'

  .prt-add-expense
      display: flex
      justify-content: center
      align-items:flex-end

    .card
      width: calc(100% - 32px)
      max-width: 504px
      border-radius: 3px
      background-color: white
      box-shadow: 0 2px 4px black-12
      margin-bottom: 16px
      overflow: hidden

      &.slide-enter-active,
      &.slide-leave-active
        transition: transform 200ms ease

      &.slide-enter
        transform: translateY(100%)

      &.slide-leave-to
        transform: translateY(-128px)

      input
        height: 48px
        width: 100%
        padding: 0 14px
        font-family: 'PT Sans', sans-serif
        font-size: 16px
        border: none
        overflow: hidden
        text-overflow: ellipsis
        display: block
        margin: 0
        outline: none
        box-sizing: border-box
        background-color: transparent
        color: black-87

        &::placeholder
          color: black-54

        &[type="number"]
          text-align: right
          -moz-appearance: textfield

          &::-webkit-outer-spin-button,
          &::-webkit-inner-spin-button
            -webkit-appearance: none
            margin: 0

      .amount-wrapper
        display: flex
        padding-left: 2px
        border-top: 1px solid bg

      .icon-selector-button
        color: positive
        width: 42px
        height: @width
        border-radius: (@width / 2)px
        border: none
        outline: none
        flex-shrink: 0
        margin-top: 3px
        padding: 0
        background-color: transparent
        opacity: 0.38
        cursor: pointer
        -webkit-tap-highlight-color: transparent
        transition: background-color 200ms ease, opacity 200ms ease

        &:active
          background-color: bg

        &.active
          opacity: 1

      .icon-wrapper
        padding-left: 2px
        border-top: 1px solid bg
        position: relative

        .icons
          display: flex
          overflow-x: auto

        .fader
          position: absolute
          top: 0px
          bottom: 0px
          width: 14px

          &.left
            left: 0px
            background-image: linear-gradient(to left, rgba(255,255,255,0), white)

          &.right
            background-image: linear-gradient(to right, rgba(255,255,255,0), white)
            right: 0px

      .button-wrapper
        height: 48px
        border-top: 1px solid bg
        display: flex

        button
          height: 48px
          border: none
          background-color: transparent
          width: 100%
          font-family: 'PT Sans', sans-serif
          font-size: 16px
          outline: none
          padding: 0
          font-weight: bold
          cursor: pointer
          -webkit-tap-highlight-color: transparent
          transition: background-color 200ms ease, color 200ms ease

          &.cancel-button
            color: negative
            border-right: 1px solid bg

            &:active
              background-color: negative
              color: white

          &.add-button
            color: positive

            &:active
              background-color: positive
              color: white

          &:disabled
            color: black-38
</style>
